/**
 * Shared data-table wrapper used across feature screens for list, search, filter, selection,
 * sorting, pagination, and optional detail-drawer behavior.
 *
 * PARENT-CHILD CONTRACT:
 * Parents configure columns, rows, and optional toolbar/detail behaviors.
 * The table owns transient UI state such as the search query, drawer state, sorting, and paging,
 * then emits normalized events back to the parent when feature-level state must change.
 */
import { ChangeDetectionStrategy, Component, computed, effect, ElementRef, inject, input, output, signal, AfterViewChecked, HostListener } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { DrawerModule } from 'primeng/drawer';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';

import { normalizeDrawerLabel } from '@shared/utils';
import { UiAutocompleteFieldComponent } from '../ui-autocomplete-field/ui-autocomplete-field.component';
import { UiAutocompleteOption } from '../ui-autocomplete-field/ui-autocomplete-field.types';
import { UiButtonComponent } from '../ui-button/ui-button.component';
import { UiEmptyStateComponent } from '../ui-empty-state/ui-empty-state.component';
import { UiFormFieldComponent } from '../ui-form-field/ui-form-field.component';
import { UiLabelTranslatePipe } from '../ui-label-translate.pipe';
import { UiResourceSummaryComponent } from '../ui-resource-summary/ui-resource-summary.component';
import { UiSelectComponent } from '../ui-select/ui-select.component';
import { UiSelectOption } from '../ui-select/ui-select.types';
import { UiToggleComponent } from '../ui-toggle/ui-toggle.component';
import {
  UiTableAction,
  UiTableActionClick,
  UiTableBadgeRule,
  UiTableBadgeTone,
  UiTableCellClick,
  UiTableColumn,
  UiTableDetailField,
  UiTableDetailMode,
  UiTableDetailSection,
  UiTableDetailTab,
  UiTableFilterField,
  UiTableFilterSearchChange,
  UiTableFilterValue,
  UiTableFirstColumnHoverMode,
  UiTableResourceSummaryItems,
  UiTableSelectChange,
  UiTableSelectionChange,
  UiTableSortChange,
  UiTableSortOrder,
  UiTableToggleChange
} from './ui-table.types';

type UiTableDrawerType = 'none' | 'filter' | 'detail';

@Component({
  selector: 'ui-table',
  standalone: true,
  imports: [FormsModule, TableModule, DrawerModule, SelectModule, ButtonModule, UiAutocompleteFieldComponent, UiButtonComponent, UiEmptyStateComponent, UiFormFieldComponent, UiLabelTranslatePipe, UiResourceSummaryComponent, UiSelectComponent, UiToggleComponent],
  templateUrl: './ui-table.component.html',
  styleUrl: './ui-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiTableComponent implements AfterViewChecked {
  private static readonly SUPPORTED_PAGE_SIZE_OPTIONS = [10, 15, 20, 50, 100] as const;
  private static readonly VISIBLE_BODY_ROW_LIMIT = 10;
  private readonly host = inject(ElementRef<HTMLElement>);

  // PARENT-CHILD CONTRACT: the parent defines row shape and visible table capabilities.
  readonly columns = input<UiTableColumn[]>([]);
  readonly data = input<unknown[]>([]);
  readonly ariaLabel = input('Data table');
  readonly emptyMessage = input('No records found.');

  readonly showToolbar = input(true);
  readonly showSearch = input(true);
  readonly showSort = input(true);
  readonly showFilter = input(true);
  readonly showPagination = input(false);
  readonly currentPage = input<number | null>(null);
  readonly defaultPageSize = input(10);
  readonly pageSizeOptions = input<number[]>([10, 15, 20, 50, 100]);
  readonly showPageSizeSelector = input(true);
  readonly serverSide = input(false);
  readonly serverSearch = input(false);
  readonly serverTotalCount = input(0);
  readonly showSelection = input(false);
  readonly selectionKey = input('id');
  readonly showBulkDeleteButton = input(false);
  readonly bulkDeleteLabel = input('Delete All');
  readonly showActionsColumn = input(false);
  readonly actionsColumnLabel = input('Actions');
  readonly rowActions = input<UiTableAction[]>([
    { id: 'edit', label: 'Edit', variant: 'ghost' },
    { id: 'delete', label: 'Delete', variant: 'danger' }
  ]);
  readonly searchPlaceholder = input('Search...');
  readonly searchKeys = input<string[]>([]);
  readonly firstColumnHover = input<UiTableFirstColumnHoverMode>('hover');
  readonly badgeRules = input<Record<string, UiTableBadgeRule>>({
    active: { tone: 'green' },
    approved: { tone: 'green' },
    ok: { tone: 'green' },
    done: { tone: 'green' },
    completed: { tone: 'green' },
    inactive: { tone: 'neutral' },
    low: { tone: 'yellow' },
    medium: { tone: 'yellow' },
    pending: { tone: 'yellow' },
    'in-progress': { tone: 'blue' },
    review: { tone: 'blue' },
    rejected: { tone: 'red' },
    'out-of-stock': { tone: 'red' },
    overdue: { tone: 'red' },
    high: { tone: 'red' },
    field: { tone: 'yellow' },
    'field-agent': { tone: 'yellow' },
    agency: { tone: 'green' },
    'agency-agent': { tone: 'green' }
  });

  readonly defaultSortKey = input('');
  readonly defaultSortOrder = input<UiTableSortOrder>('asc');

  readonly filterFields = input<UiTableFilterField[]>([]);

  readonly rowClickable = input(false);
  readonly detailTitleKey = input('name');
  readonly detailFields = input<UiTableDetailField[]>([]);
  readonly detailTabs = input<UiTableDetailTab[]>([]);
  readonly detailMode = input<UiTableDetailMode>('side-panel');

  readonly cellClick = output<UiTableCellClick>();
  readonly rowClick = output<unknown>();
  readonly actionClick = output<UiTableActionClick>();
  readonly toggleChange = output<UiTableToggleChange>();
  readonly selectChange = output<UiTableSelectChange>();
  readonly sortChange = output<UiTableSortChange>();
  readonly selectionChange = output<UiTableSelectionChange>();
  readonly bulkDelete = output<unknown[]>();
  readonly filtersAppliedChange = output<Record<string, UiTableFilterValue>>();
  readonly filtersReset = output<void>();
  readonly filterLoadMore = output<string>();
  readonly filterSearchChange = output<UiTableFilterSearchChange>();
  readonly searchChange = output<string>();
  readonly viewDocuments = output<unknown>();
  readonly pageChange = output<{ page: number; pageSize: number }>();

  // UI state owned locally by the table so every screen gets the same interaction model.
  readonly searchQuery = signal('');
  readonly sortKey = signal('');
  readonly sortOrder = signal<UiTableSortOrder>('asc');
  readonly selectedRow = signal<unknown | null>(null);
  readonly selectedRowKeys = signal<string[]>([]);
  readonly pageIndex = signal(0);
  readonly pageSize = signal(10);
  readonly activeDrawer = signal<UiTableDrawerType>('none');
  readonly activeDetailTabId = signal('');
  readonly openDropdownRowKey = signal<string | null>(null);

  // Filters are split into draft vs applied state so the drawer can be edited before commit.
  readonly filterDraft = signal<Record<string, UiTableFilterValue>>({});
  readonly filterApplied = signal<Record<string, UiTableFilterValue>>({});
  readonly appliedFilterCount = computed(() => {
    const state = this.filterApplied();
    let count = 0;

    for (const value of Object.values(state)) {
      if (Array.isArray(value)) {
        if (value.length > 0) {
          count += 1;
        }

        continue;
      }

      if (typeof value === 'boolean') {
        if (value) {
          count += 1;
        }

        continue;
      }

      if (String(value).trim() !== '') {
        count += 1;
      }
    }

    return count;
  });

  readonly hasActiveFilters = computed(() => this.appliedFilterCount() > 0);
  readonly hasDataRows = computed(() => this.data().length > 0);
  readonly shouldShowToolbar = computed(() =>
    this.showToolbar()
    && (
      this.hasDataRows()
      || this.hasActiveFilters()
      || this.searchQuery().trim().length > 0
    )
  );

  readonly sortableColumns = computed(() => this.columns().filter((column) => column.sortable));

  /**
   * Derives the client-side result set after applying the shared search, filter, and sort rules.
   * Parents can opt out of client paging by enabling server-side mode.
   */
  readonly processedRows = computed(() => {
    const query = this.serverSide() || this.serverSearch()
      ? ''
      : this.searchQuery().trim().toLowerCase();
    const applied = this.filterApplied();

    const searched = this.data().filter((row) => {
      const matchesSearch = !query || this.matchesSearch(row, query);
      const matchesFilter = this.matchesAllFilters(row, applied);
      return matchesSearch && matchesFilter;
    });

    const sortKey = this.activeSortKey();

    if (!sortKey) {
      return searched;
    }

    const order = this.sortOrder();

    return [...searched].sort((leftRow, rightRow) => {
      const left = this.valueOf(leftRow, sortKey);
      const right = this.valueOf(rightRow, sortKey);
      const result = String(left ?? '').localeCompare(String(right ?? ''), undefined, { numeric: true });
      return order === 'asc' ? result : -result;
    });
  });

  readonly pagedRows = computed(() => {
    if (this.serverSide()) {
      return this.data() as unknown[];
    }

    if (!this.showPagination()) {
      return this.processedRows();
    }

    const start = this.pageIndex() * this.pageSize();
    return this.processedRows().slice(start, start + this.pageSize());
  });

  readonly pageSizeSelectorOptions = computed(() => this.resolvedPageSizeOptions());
  readonly pageSizeValue = computed(() => `${this.pageSize()}`);

  readonly pageMeta = computed(() => {
    const total = this.serverSide() ? this.serverTotalCount() : this.processedRows().length;

    if (total === 0) {
      return { total: 0, start: 0, end: 0 };
    }

    if (!this.showPagination()) {
      return { total, start: 1, end: total };
    }

    const start = this.pageIndex() * this.pageSize() + 1;
    const end = Math.min(total, this.pageIndex() * this.pageSize() + this.pageSize());
    return { total, start, end };
  });

  readonly totalPages = computed(() => {
    if (!this.showPagination()) {
      return 1;
    }

    const total = this.serverSide() ? this.serverTotalCount() : this.processedRows().length;
    return Math.max(1, Math.ceil(total / this.pageSize()));
  });

  readonly selectedRowTitle = computed(() => {
    const row = this.selectedRow();

    if (!row) {
      return '';
    }

    const value = this.valueOf(row, this.detailTitleKey());
    return value === null || value === undefined ? '' : String(value);
  });

  readonly resolvedDetailTabs = computed<UiTableDetailTab[]>(() => {
    const tabs = this.detailTabs();

    if (tabs.length > 0) {
      return tabs;
    }

    if (this.detailFields().length === 0) {
      return [];
    }

    return [
      {
        id: 'overview',
        label: 'Overview',
        fields: this.detailFields()
      }
    ];
  });

  readonly activeDetailTab = computed(() => {
    const tabs = this.resolvedDetailTabs();

    if (tabs.length === 0) {
      return null;
    }

    const active = this.activeDetailTabId();
    return tabs.find((tab) => tab.id === active) ?? tabs[0];
  });

  readonly hasDetail = computed(() => this.resolvedDetailTabs().length > 0);
  readonly isRowClickable = computed(() => this.rowClickable());
  readonly displayedRows = computed(() => this.pagedRows());
  /**
   * BUSINESS LOGIC: drives the empty-state vs table-body switch. In server-side mode the backend
   * already applied search/filter/pagination, so visibility must follow the rendered rows
   * (`pagedRows`) rather than the client-side `processedRows`. Using `processedRows` here hid
   * server-filtered results because the client filter values (e.g. agency id, posType casing) did
   * not re-match the returned rows.
   */
  readonly hasVisibleRows = computed(() =>
    this.serverSide() ? this.pagedRows().length > 0 : this.processedRows().length > 0
  );
  readonly firstDataColumnKey = computed(() => this.columns()[0]?.key ?? '');
  readonly isFilterDrawerOpen = computed(() => this.activeDrawer() === 'filter');
  readonly isDetailDrawerOpen = computed(() => this.activeDrawer() === 'detail');
  readonly isAnyDrawerOpen = computed(() => this.activeDrawer() !== 'none');
  readonly shouldScrollBody = computed(() =>
    this.showPagination() || this.pagedRows().length > UiTableComponent.VISIBLE_BODY_ROW_LIMIT
  );

  readonly selectedRows = computed(() => {
    const selected = new Set(this.selectedRowKeys());
    return this.data().filter((row) => selected.has(this.rowKey(row)));
  });

  readonly allDisplayedSelected = computed(() => {
    const displayed = this.displayedRows();

    if (displayed.length === 0) {
      return false;
    }

    const selected = new Set(this.selectedRowKeys());
    return displayed.every((row) => selected.has(this.rowKey(row)));
  });

  readonly normalizeDrawerLabel = normalizeDrawerLabel;

  /**
   * Seeds shared defaults so every feature screen starts from the same sorting and page-size rules.
   */
  constructor() {
    const initialSortKey = this.defaultSortKey();
    const fallbackSortKey = this.sortableColumns()[0]?.key ?? '';
    const resolvedSortKey = initialSortKey || fallbackSortKey;

    this.sortKey.set(resolvedSortKey);
    this.sortOrder.set(this.defaultSortOrder());
    // BUSINESS LOGIC: seed the initial page size from the resolved default so server-side lists,
    // emitted page events, and visible pagination all agree on first render.
    this.pageSize.set(this.resolveInitialPageSize());

    effect(() => {
      const availablePageSizes = this.resolvedPageSizeOptions();
      const currentPageSize = this.pageSize();
      const resolvedPageSize = availablePageSizes.includes(currentPageSize)
        ? currentPageSize
        : this.resolveInitialPageSize();

      if (currentPageSize !== resolvedPageSize) {
        this.pageSize.set(resolvedPageSize);
        this.pageIndex.set(0);
      }
    });

    effect(() => {
      const currentPage = this.currentPage();
      if (!this.showPagination() || !Number.isFinite(currentPage) || (currentPage ?? 0) <= 0) {
        return;
      }

      const nextPageIndex = Math.floor((currentPage as number) - 1);
      if (this.pageIndex() !== nextPageIndex) {
        this.pageIndex.set(nextPageIndex);
      }
    });
  }

  ngAfterViewChecked(): void {
    this.syncMeasuredViewportHeight();
  }

  /**
   * Updates local search state and informs the parent when a server-side query should be reloaded.
   */
  onSearchChange(value: string): void {
    this.searchQuery.set(value);
    this.pageIndex.set(0);
    this.searchChange.emit(value);
  }

  openFilterPanel(): void {
    this.filterDraft.set({ ...this.filterApplied() });
    this.selectedRow.set(null);
    this.activeDrawer.set('filter');
  }

  closeFilterPanel(): void {
    if (this.activeDrawer() === 'filter') {
      this.activeDrawer.set('none');
    }
  }

  private syncMeasuredViewportHeight(): void {
    const host = this.host.nativeElement;
    const headRow = host.querySelector('.ui-table thead tr') as HTMLElement | null;
    const bodyRow =
      (host.querySelector('.ui-table tbody tr:not(.ui-table__empty-row)') as HTMLElement | null) ??
      (host.querySelector('.ui-table tbody tr') as HTMLElement | null);

    if (!headRow || !bodyRow) {
      return;
    }

    const headHeight = Math.ceil(headRow.getBoundingClientRect().height);
    const rowHeight = Math.ceil(bodyRow.getBoundingClientRect().height);

    if (headHeight > 0) {
      host.style.setProperty('--ui-table-head-height', `${headHeight}px`);
    }

    if (rowHeight > 0) {
      host.style.setProperty('--ui-table-row-height', `${rowHeight}px`);
    }
  }

  openDetailPanel(row: unknown): void {
    if (!this.hasDetail() || this.detailMode() === 'none') {
      return;
    }

    this.selectedRow.set(row);

    const firstTab = this.resolvedDetailTabs()[0];
    this.activeDetailTabId.set(firstTab?.id ?? '');
    this.activeDrawer.set('detail');
  }

  closeDetailPanel(): void {
    if (this.activeDrawer() === 'detail') {
      this.activeDrawer.set('none');
    }

    this.selectedRow.set(null);
  }

  closeActiveDrawer(): void {
    const current = this.activeDrawer();

    if (current === 'filter') {
      this.closeFilterPanel();
      return;
    }

    if (current === 'detail') {
      this.closeDetailPanel();
    }
  }

  selectDetailTab(tabId: string): void {
    this.activeDetailTabId.set(tabId);
  }

  /**
   * Updates draft filter text without applying it immediately.
   * PARENT-CHILD CONTRACT: dependent reset keys let feature screens model chained filter fields.
   */
  setFilterDraftText(fieldKey: string, value: string): void {
    const field = this.filterFields().find((item) => item.key === fieldKey);

    this.filterDraft.update((state) => {
      const nextState: Record<string, UiTableFilterValue> = { ...state, [fieldKey]: value };

      for (const resetKey of field?.resetKeysOnChange ?? []) {
        nextState[resetKey] = '';
      }

      return nextState;
    });
  }

  setFilterDraftValues(fieldKey: string, values: string[]): void {
    this.filterDraft.update((state) => ({ ...state, [fieldKey]: values }));
  }

  setFilterDraftToggle(fieldKey: string, value: boolean): void {
    this.filterDraft.update((state) => ({ ...state, [fieldKey]: value }));
  }

  /**
   * Commits the current filter draft and emits one normalized filter payload for the parent.
   */
  applyFilters(): void {
    const nextFilters = { ...this.filterDraft() };
    this.filterApplied.set(nextFilters);
    this.filtersAppliedChange.emit(nextFilters);
    this.pageIndex.set(0);
    this.closeFilterPanel();
  }

  resetFilters(): void {
    this.filterDraft.set({});
    this.filterApplied.set({});
    this.filtersAppliedChange.emit({});
    this.filtersReset.emit();
    this.pageIndex.set(0);
    this.closeFilterPanel();
  }

  toggleColumnSort(column: UiTableColumn): void {
    if (!column.sortable) {
      return;
    }

    if (this.sortKey() === column.key) {
      this.sortOrder.set(this.sortOrder() === 'asc' ? 'desc' : 'asc');
    } else {
      this.sortKey.set(column.key);
      this.sortOrder.set('asc');
    }

    this.sortChange.emit({ key: this.sortKey(), order: this.sortOrder() });
    this.pageIndex.set(0);
  }

  headerSortState(column: UiTableColumn): 'ascending' | 'descending' | 'none' {
    if (!column.sortable || !this.showSort()) {
      return 'none';
    }

    if (this.sortKey() !== column.key) {
      return 'none';
    }

    return this.sortOrder() === 'asc' ? 'ascending' : 'descending';
  }

  onSortHeaderKeydown(event: KeyboardEvent, column: UiTableColumn): void {
    if (!column.sortable || !this.showSort()) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggleColumnSort(column);
    }
  }

  badgeToneFor(raw: string): UiTableBadgeTone {
    const rule = this.badgeRules()[this.normalizeBadgeKey(raw)];
    return rule?.tone ?? 'neutral';
  }

  badgeBgFor(raw: string): string | null {
    const rule = this.badgeRules()[this.normalizeBadgeKey(raw)];
    return rule?.colors ? `var(${rule.colors.backgroundVar})` : null;
  }

  badgeTextFor(raw: string): string | null {
    const rule = this.badgeRules()[this.normalizeBadgeKey(raw)];
    return rule?.colors ? `var(${rule.colors.textVar})` : null;
  }

  onDataCellClick(event: Event, column: UiTableColumn, row: unknown): void {
    const target = event.target as HTMLElement | null;

    if (target && this.isInteractiveTarget(target)) {
      return;
    }

    if (!this.isFirstColumnInteractive(column)) {
      return;
    }

    this.emitFirstColumnClick(column, row);
  }

  toggleAllDisplayed(checked: boolean): void {
    const selected = new Set(this.selectedRowKeys());

    if (checked) {
      this.displayedRows().forEach((row) => selected.add(this.rowKey(row)));
    } else {
      this.displayedRows().forEach((row) => selected.delete(this.rowKey(row)));
    }

    this.selectedRowKeys.set([...selected]);
    this.selectionChange.emit({ selectedRows: this.selectedRows() });
  }

  toggleRowSelection(event: Event, row: unknown, checked: boolean): void {
    event.stopPropagation();

    const key = this.rowKey(row);
    const selected = new Set(this.selectedRowKeys());

    if (checked) {
      selected.add(key);
    } else {
      selected.delete(key);
    }

    this.selectedRowKeys.set([...selected]);
    this.selectionChange.emit({ selectedRows: this.selectedRows() });
  }

  isRowSelected(row: unknown): boolean {
    return this.selectedRowKeys().includes(this.rowKey(row));
  }

  triggerBulkDelete(): void {
    this.bulkDelete.emit(this.selectedRows());
  }

  setPageSize(value: string): void {
    const parsed = Number(value);
    const supportedSizes = this.resolvedPageSizeOptions();

    if (!Number.isFinite(parsed) || !supportedSizes.includes(parsed)) {
      return;
    }

    this.pageSize.set(parsed);
    this.pageIndex.set(0);

    if (this.serverSide()) {
      this.pageChange.emit({ page: 1, pageSize: parsed });
    }
  }

  onPageSizeSelectChange(event: Event): void {
    const select = event.target as HTMLSelectElement | null;
    this.setPageSize(select?.value ?? '');
  }

  previousPage(): void {
    const next = this.pageIndex() - 1;
    this.pageIndex.set(next < 0 ? 0 : next);

    if (this.serverSide()) {
      this.pageChange.emit({ page: this.pageIndex() + 1, pageSize: this.pageSize() });
    }
  }

  nextPage(): void {
    const max = this.totalPages() - 1;
    const next = this.pageIndex() + 1;
    this.pageIndex.set(next > max ? max : next);

    if (this.serverSide()) {
      this.pageChange.emit({ page: this.pageIndex() + 1, pageSize: this.pageSize() });
    }
  }

  canGoPrevious(): boolean {
    return this.pageIndex() > 0;
  }

  canGoNext(): boolean {
    return this.pageIndex() < this.totalPages() - 1;
  }

  onCellClick(event: Event, column: UiTableColumn, row: unknown): void {
    event.stopPropagation();

    if (!column.clickable || !this.isFirstColumn(column)) {
      return;
    }

    this.emitFirstColumnClick(column, row);
  }

  onActionClick(event: Event, actionId: string, row: unknown): void {
    event.stopPropagation();
    this.actionClick.emit({ actionId, row });
  }

  onToggleChange(column: UiTableColumn, row: unknown, checked: boolean): void {
    this.toggleChange.emit({ key: column.key, row, checked });
  }

  onSelectChange(column: UiTableColumn, row: unknown, value: string): void {
    this.selectChange.emit({ key: column.key, row, value });
  }

  isIconOnlyAction(action: UiTableAction): boolean {
    return Boolean(this.actionIconSymbol(action));
  }

  actionIconSymbol(action: UiTableAction): string {
    if (action.iconSymbol) {
      return action.iconSymbol.trim();
    }

    const kind = this.actionIconKind(action);

    if (kind === 'edit') {
      return 'edit';
    }

    if (kind === 'delete') {
      return 'delete';
    }

    if (kind === 'menu') {
      return 'more_vert';
    }

    if (kind === 'approve') {
      return 'done';
    }

    if (kind === 'reject') {
      return 'close';
    }

    return '';
  }

  sortIconSymbol(column: UiTableColumn): string {
    switch (this.headerSortState(column)) {
      case 'ascending':
        return 'arrow_upward';
      case 'descending':
        return 'arrow_downward';
      default:
        return 'arrow_upward';
    }
  }

  private actionIconKind(action: UiTableAction): 'edit' | 'delete' | 'menu' | 'approve' | 'reject' | null {
    const key = action.id.trim().toLowerCase();

    if (key === 'edit' || key.startsWith('edit-') || key.endsWith('-edit')) {
      return 'edit';
    }

    if (key === 'delete' || key.startsWith('delete-') || key.endsWith('-delete')) {
      return 'delete';
    }

    if (key === 'menu' || key === 'manage' || key.startsWith('manage-') || key.endsWith('-menu')) {
      return 'menu';
    }

    if (key.startsWith('approve-') || key.endsWith('-approve') || key === 'approve') {
      return 'approve';
    }

    if (key.startsWith('reject-') || key.endsWith('-reject') || key === 'reject') {
      return 'reject';
    }

    return null;
  }

  trackRow(index: number, row: unknown): unknown {
    if (typeof row !== 'object' || row === null) {
      return index;
    }

    const id = (row as Record<string, unknown>)['id'];
    return id ?? index;
  }

  rowKey(row: unknown): string {
    const key = this.selectionKey();
    const value = this.valueOf(row, key);

    return value === null || value === undefined ? String(this.trackRow(0, row)) : String(value);
  }

  valueOf(row: unknown, key: string): unknown {
    if (typeof row !== 'object' || row === null) {
      return '';
    }

    return (row as Record<string, unknown>)[key];
  }

  getDocuments(row: unknown): Array<{ id: string; name: string; type: string; url: string }> {
    if (typeof row !== 'object' || row === null) return [];
    const docs = (row as Record<string, unknown>)['documents'];
    if (!Array.isArray(docs)) return [];
    return docs as Array<{ id: string; name: string; type: string; url: string }>;
  }

  getDetailTableRows(row: unknown, field: UiTableDetailField): unknown[] {
    if (field.tableRows) return field.tableRows(row);
    if (typeof row !== 'object' || row === null) return [];
    const value = (row as Record<string, unknown>)[field.key];
    return Array.isArray(value) ? value : [];
  }

  resourceSummaryItemsOf(row: unknown, column: UiTableColumn): UiTableResourceSummaryItems {
    const value = this.valueOf(row, column.key);
    return Array.isArray(value) ? value as UiTableResourceSummaryItems : [];
  }

  detailTableBadgeToneFor(raw: string, field: UiTableDetailField): UiTableBadgeTone {
    const rules = field.tableBadgeRules ?? this.badgeRules();
    const rule = rules[this.normalizeBadgeKey(raw)];
    return (rule as UiTableBadgeRule | undefined)?.tone ?? 'neutral';
  }

  textOf(row: unknown, column: UiTableColumn): string {
    const value = this.valueOf(row, column.key);

    if (column.formatter) {
      return column.formatter(value, row);
    }

    return value === null || value === undefined ? '' : String(value);
  }

  detailTextOf(row: unknown, field: UiTableDetailField): string {
    const value = this.valueOf(row, field.key);

    if (field.formatter) {
      return field.formatter(value, row);
    }

    return value === null || value === undefined ? '' : String(value);
  }

  detailSectionsFor(tab: UiTableDetailTab): UiTableDetailSection[] {
    if (tab.sections && tab.sections.length > 0) {
      return tab.sections;
    }

    if (tab.fields && tab.fields.length > 0) {
      return [
        {
          id: `${tab.id}-default`,
          fields: tab.fields
        }
      ];
    }

    return [];
  }

  filterValue(fieldKey: string): UiTableFilterValue {
    return this.filterDraft()[fieldKey] ?? '';
  }

  filterValues(fieldKey: string): string[] {
    const value = this.filterValue(fieldKey);
    return Array.isArray(value) ? value : [];
  }

  asText(value: UiTableFilterValue): string {
    return String(value);
  }

  filterOptions(field: UiTableFilterField): Array<{ label: string; value: string; caption?: string }> {
    const options = field.options;

    if (!options) {
      return [];
    }

    return typeof options === 'function' ? options(this.filterDraft()) : options;
  }

  filterAutocompleteOptions(field: UiTableFilterField): UiAutocompleteOption[] {
    return this.filterOptions(field).map((option) => ({
      label: option.label,
      value: option.value,
      caption: option.caption
    }));
  }

  isFilterFieldDisabled(field: UiTableFilterField): boolean {
    const disabled = field.disabled;
    return typeof disabled === 'function' ? disabled(this.filterDraft()) : Boolean(disabled);
  }

  isActionDisabled(action: { disabled?: (row: unknown) => boolean }, row: unknown): boolean {
    return action.disabled ? action.disabled(row) : false;
  }

  isToggleChecked(column: UiTableColumn, row: unknown): boolean {
    return column.toggle?.checked ? column.toggle.checked(row) : false;
  }

  isToggleDisabled(column: UiTableColumn, row: unknown): boolean {
    return column.toggle?.disabled ? column.toggle.disabled(row) : false;
  }

  toggleAriaLabel(column: UiTableColumn, row: unknown): string {
    return column.toggle?.ariaLabel ? column.toggle.ariaLabel(row) : column.label;
  }

  selectOptions(column: UiTableColumn, row: unknown): UiSelectOption[] {
    if (!column.select?.options) {
      return [];
    }

    return typeof column.select.options === 'function' ? column.select.options(row) : column.select.options;
  }

  selectValue(column: UiTableColumn, row: unknown): string {
    if (column.select?.value) {
      return column.select.value(row);
    }

    const value = this.valueOf(row, column.key);
    return value === null || value === undefined ? '' : String(value);
  }

  loadMoreFilterOptions(field: UiTableFilterField): void {
    this.filterLoadMore.emit(field.key);
  }

  onFilterSearchChange(field: UiTableFilterField, query: string): void {
    this.filterSearchChange.emit({ key: field.key, query });
  }

  isSelectDisabled(column: UiTableColumn, row: unknown): boolean {
    return column.select?.disabled ? column.select.disabled(row) : false;
  }

  activeSortKey(): string {
    const key = this.sortKey();

    if (key) {
      return key;
    }

    return this.defaultSortKey() || this.sortableColumns()[0]?.key || '';
  }

  private matchesSearch(row: unknown, query: string): boolean {
    const keys = this.searchKeys();

    if (keys.length > 0) {
      return keys.some((key) => String(this.valueOf(row, key)).toLowerCase().includes(query));
    }

    return this.columns().some((column) => String(this.valueOf(row, column.key)).toLowerCase().includes(query));
  }

  private matchesAllFilters(row: unknown, filters: Record<string, UiTableFilterValue>): boolean {
    const fields = this.filterFields();

    return fields.every((field) => {
      const filterValue = filters[field.key];

      if (field.type === 'toggle') {
        if (filterValue !== true) {
          return true;
        }

        return Boolean(this.valueOf(row, field.key));
      }

      if (filterValue === undefined || filterValue === '') {
        return true;
      }

      const rowValue = this.valueOf(row, field.key);

      if (field.type === 'multiselect') {
        if (!Array.isArray(filterValue) || filterValue.length === 0) {
          return true;
        }

        return filterValue.includes(String(rowValue ?? ''));
      }

      if (field.type === 'text' || field.type === 'autocomplete') {
        return String(rowValue ?? '').toLowerCase().includes(String(filterValue).toLowerCase());
      }

      if (field.type === 'number') {
        return Number(rowValue) === Number(filterValue);
      }

      return String(rowValue ?? '') === String(filterValue);
    });
  }

  private normalizeBadgeKey(value: string): string {
    return value.trim().toLowerCase().replace(/\s+/g, '-');
  }

  isFirstColumn(column: UiTableColumn): boolean {
    return column.key === this.firstDataColumnKey();
  }

  shouldShowFirstColumnHover(column: UiTableColumn): boolean {
    return this.firstColumnHover() === 'hover' && this.isFirstColumn(column);
  }

  isFirstColumnInteractive(column: UiTableColumn): boolean {
    return this.isFirstColumn(column) && (column.clickable === true || this.isRowClickable());
  }

  private emitFirstColumnClick(column: UiTableColumn, row: unknown): void {
    this.cellClick.emit({ key: column.key, row });

    if (this.hasDetail() && this.detailMode() === 'side-panel') {
      this.openDetailPanel(row);
    }
  }

  private resolveInitialPageSize(): number {
    const options = this.resolvedPageSizeOptions();
    const defaultSize = this.defaultPageSize();

    if (defaultSize > 0 && options.includes(defaultSize)) {
      return defaultSize;
    }

    return options[0] ?? 10;
  }

  private resolvedPageSizeOptions(): number[] {
    // Keep shared page-size options strict while preserving the project default of 10 rows.
    const supported = new Set<number>(UiTableComponent.SUPPORTED_PAGE_SIZE_OPTIONS);
    const requested = this.pageSizeOptions().filter((value) => supported.has(value));
    const uniqueRequested = [...new Set(requested)];

    if (!uniqueRequested.includes(10)) {
      uniqueRequested.push(10);
    }

    if (uniqueRequested.length === 0) {
      return [10, 15, 20, 50, 100];
    }

    return uniqueRequested.sort((left, right) => left - right);
  }

  visibleActions(actions: UiTableAction[]): UiTableAction[] {
    if (actions.length <= 2) return actions;
    return actions.filter((a) => {
      const k = a.id.trim().toLowerCase();
      return k === 'edit' || k.startsWith('edit-') || k.endsWith('-edit') ||
             k === 'delete' || k.startsWith('delete-') || k.endsWith('-delete');
    }).slice(0, 2);
  }

  overflowActions(actions: UiTableAction[]): UiTableAction[] {
    if (actions.length <= 2) return [];
    const visible = new Set(this.visibleActions(actions).map((a) => a.id));
    return actions.filter((a) => !visible.has(a.id));
  }

  isDropdownOpen(row: unknown): boolean {
    return this.openDropdownRowKey() === this.rowKey(row);
  }

  toggleDropdown(event: Event, row: unknown): void {
    event.stopPropagation();
    const key = this.rowKey(row);
    this.openDropdownRowKey.set(this.openDropdownRowKey() === key ? null : key);
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.openDropdownRowKey.set(null);
  }

  closeDropdown(): void {
    this.openDropdownRowKey.set(null);
  }

  onDropdownActionClick(event: Event, actionId: string, row: unknown): void {
    this.closeDropdown();
    this.onActionClick(event, actionId, row);
  }

  private isInteractiveTarget(target: HTMLElement): boolean {
    return Boolean(
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input') ||
      target.closest('select') ||
      target.closest('textarea') ||
      target.closest('.ui-table__actions') ||
      target.closest('.ui-table__selection-cell') ||
      target.closest('.ui-table__link') ||
      target.closest('.ui-table__overflow-dropdown') ||
      target.closest('ui-toggle') ||
      target.closest('.ui-toggle')
    );
  }
}
